import { z } from 'zod';

/**
 * The permission catalogue, shared by both surfaces.
 *
 * One shape, two sources: `/users/permissions` describes a merchant's own
 * modules, `/platform/staff/permissions` describes the platform's. They are
 * deliberately different *lists* served in the same *envelope*, which is what
 * lets one editor render either - see `PermissionEditor`.
 *
 * Mirrors `PermissionCatalogueResponse` in `app/schemas/team.py`.
 */

export const moduleActionSchema = z.object({
  label: z.string(),
  /**
   * Never assignable, on either surface: held by the merchant's owner, or by a
   * platform super admin. Listed anyway so the capability is findable rather
   * than looking as though it does not exist.
   */
  permission: z.string(),
  owner_only: z.boolean(),
});

export const permissionModuleSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  actions: z.array(moduleActionSchema),
  always_available: z.boolean(),
});

export const roleDescriptionSchema = z.object({
  role: z.string(),
  label: z.string(),
  description: z.string(),
  /** What the role grants by default, so "reset" needs no round trip. */
  permissions: z.array(z.string()),
  assignable: z.boolean(),
});

export const permissionCatalogueSchema = z.object({
  modules: z.array(permissionModuleSchema),
  roles: z.array(roleDescriptionSchema),
});

export type PermissionCatalogue = z.infer<typeof permissionCatalogueSchema>;
export type PermissionModule = z.infer<typeof permissionModuleSchema>;
export type RoleDescription = z.infer<typeof roleDescriptionSchema>;

/** The permissions a role grants by default, from the catalogue the API sent. */
export function defaultsFor(catalogue: PermissionCatalogue, role: string): Set<string> {
  const entry = catalogue.roles.find((candidate) => candidate.role === role);
  return new Set(entry?.permissions ?? []);
}

/** The roles this catalogue may assign, as the pickers list them. */
export function assignableRoles(catalogue: PermissionCatalogue) {
  return catalogue.roles
    .filter((role) => role.assignable)
    .map((role) => ({ value: role.role, label: role.label, description: role.description }));
}
